import { difference, reverseScale, scale as scalePoint, translate } from '../../../../../common/maths/geometry/point';
import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../../../common/maths/geometry/rect/rectTypes';
import { KEY_CODES } from '../../../../utils/keyboard/keyConstants';

export type ImageModalZoomState = {
    scale: number;
    translation: Point;
};

export type ImageModalZoomBounds = {
    minScale?: number;
    maxScale?: number;
};

export type ImageModalZoomDimensions = {
    viewportSize: RectSize;
    contentSize: RectSize;
};

export const DEFAULT_IMAGE_MODAL_ZOOM_STATE: ImageModalZoomState = {
    scale: 1,
    translation: {
        x: 0,
        y: 0,
    },
};
export const DEFAULT_IMAGE_MODAL_MIN_SCALE = 1;
export const DEFAULT_IMAGE_MODAL_MAX_SCALE = 4;
export const DEFAULT_IMAGE_MODAL_ZOOM_STEP = 0.1;
export const DEFAULT_IMAGE_MODAL_WHEEL_SCALE_SPEED = 0.001;

const clampNumber = (value: number, minValue: number, maxValue: number): number =>
    Math.min(maxValue, Math.max(minValue, value));

const getTranslationLimit = (viewportDimension: number, contentDimension: number, scale: number): number =>
    Math.max((contentDimension * scale - viewportDimension) / 2, 0);

const getOverflowForScale = (viewportSize: RectSize, contentSize: RectSize, scale: number): Point => ({
    x: Math.max(contentSize.width * scale - viewportSize.width, 0),
    y: Math.max(contentSize.height * scale - viewportSize.height, 0),
});

/**
 * Clamps the zoom scale to the provided bounds.
 */
export const clampZoomScale = (scale: number, { minScale = 1, maxScale = 4 }: ImageModalZoomBounds = {}): number =>
    clampNumber(scale, minScale, maxScale);

/**
 * Maps the relevant keyboard shortcuts to image modal zoom actions.
 */
export const getImageModalZoomKeyboardAction = (keyCode: number): 'zoomIn' | 'zoomOut' | 'reset' | null => {
    switch (keyCode) {
        case KEY_CODES.EQUAL:
        case KEY_CODES.ADD:
            return 'zoomIn';
        case KEY_CODES.DASH:
        case KEY_CODES.SUBTRACT:
            return 'zoomOut';
        case KEY_CODES['0']:
            return 'reset';
        default:
            return null;
    }
};

/**
 * Converts a wheel delta into an image modal zoom delta.
 */
export const getImageModalZoomScaleDeltaFromWheelDelta = (
    deltaY: number,
    currentScale: number,
    zoomSpeed = DEFAULT_IMAGE_MODAL_WHEEL_SCALE_SPEED,
) => {
    if (!deltaY) return 0;

    const clampedDeltaY = clampNumber(deltaY, -100, 100);
    return currentScale * -clampedDeltaY * zoomSpeed;
};

/**
 * Applies a zoom increment and clamps the result to the provided bounds.
 */
export const getNextZoomScale = ({
    currentScale,
    scaleDelta,
    minScale = 1,
    maxScale = 4,
}: {
    currentScale: number;
    scaleDelta: number;
} & Required<ImageModalZoomBounds>): number => clampZoomScale(currentScale + scaleDelta, { minScale, maxScale });

/**
 * Clamps the translation so the zoomed content remains within the viewport bounds.
 */
export const clampZoomTranslation = (
    translation: Point,
    { scale, viewportSize, contentSize }: ImageModalZoomState & ImageModalZoomDimensions,
): Point => {
    const xLimit = getTranslationLimit(viewportSize.width, contentSize.width, scale);
    const yLimit = getTranslationLimit(viewportSize.height, contentSize.height, scale);

    return {
        x: xLimit ? clampNumber(translation.x, -xLimit, xLimit) : 0,
        y: yLimit ? clampNumber(translation.y, -yLimit, yLimit) : 0,
    };
};

/**
 * Converts the internal centred translation representation into scroll offsets for the current scale.
 */
export const getScrollOffsetFromTranslation = ({
    contentSize,
    scale,
    translation,
    viewportSize,
}: ImageModalZoomState & ImageModalZoomDimensions): Point => {
    const overflow = getOverflowForScale(viewportSize, contentSize, scale);
    const centredOverflow = scalePoint(0.5, overflow);

    return difference(translation, centredOverflow);
};

/**
 * Converts scroll offsets back into the internal centred translation representation.
 */
export const getTranslationFromScrollOffset = ({
    contentSize,
    scale,
    scrollOffset,
    viewportSize,
}: ImageModalZoomDimensions & {
    scale: number;
    scrollOffset: Point;
}): Point => {
    const overflow = getOverflowForScale(viewportSize, contentSize, scale);
    const centredOverflow = scalePoint(0.5, overflow);

    return difference(scrollOffset, centredOverflow);
};

/**
 * Calculates the next translation that keeps the anchor point fixed while scaling.
 */
export const getZoomTranslationForScale = ({
    anchorPoint,
    contentSize,
    currentScale,
    currentTranslation,
    nextScale,
    viewportSize,
}: {
    anchorPoint: Point;
    contentSize: RectSize;
    currentScale: number;
    currentTranslation: Point;
    nextScale: number;
    viewportSize: RectSize;
}): Point => {
    if (!currentScale) return currentTranslation;

    const currentScrollOffset = getScrollOffsetFromTranslation({
        scale: currentScale,
        translation: currentTranslation,
        viewportSize,
        contentSize,
    });
    const anchorPointInUnscaledContent = reverseScale(currentScale, translate(currentScrollOffset, anchorPoint));
    const nextScrollOffset = difference(anchorPoint, scalePoint(nextScale, anchorPointInUnscaledContent));

    return getTranslationFromScrollOffset({
        scale: nextScale,
        scrollOffset: nextScrollOffset,
        viewportSize,
        contentSize,
    });
};

/**
 * Calculates the next zoom state for a scale change anchored to a viewport point.
 */
export const getNextZoomState = ({
    anchorPoint,
    bounds,
    dimensions,
    scaleDelta,
    zoomState,
}: {
    anchorPoint: Point;
    bounds?: ImageModalZoomBounds;
    dimensions: ImageModalZoomDimensions;
    scaleDelta: number;
    zoomState: ImageModalZoomState;
}): ImageModalZoomState => {
    const nextScale = getNextZoomScale({
        currentScale: zoomState.scale,
        scaleDelta,
        minScale: bounds?.minScale ?? 1,
        maxScale: bounds?.maxScale ?? 4,
    });

    const nextTranslation = getZoomTranslationForScale({
        anchorPoint,
        contentSize: dimensions.contentSize,
        currentScale: zoomState.scale,
        currentTranslation: zoomState.translation,
        nextScale,
        viewportSize: dimensions.viewportSize,
    });

    return {
        scale: nextScale,
        translation: clampZoomTranslation(nextTranslation, {
            scale: nextScale,
            translation: nextTranslation,
            ...dimensions,
        }),
    };
};

/**
 * Calculates the next anchored zoom state from the latest zoom state and a wheel delta.
 */
export const getNextZoomStateFromWheelDelta = ({
    anchorPoint,
    bounds,
    dimensions,
    wheelDeltaY,
    zoomState,
}: {
    anchorPoint: Point;
    bounds?: ImageModalZoomBounds;
    dimensions: ImageModalZoomDimensions;
    wheelDeltaY: number;
    zoomState: ImageModalZoomState;
}): ImageModalZoomState => {
    const scaleDelta = getImageModalZoomScaleDeltaFromWheelDelta(wheelDeltaY, zoomState.scale);
    if (!scaleDelta) return zoomState;

    return getNextZoomState({
        anchorPoint,
        bounds,
        dimensions,
        scaleDelta,
        zoomState,
    });
};

/**
 * Calculates the next zoom state after a pan delta has been applied.
 */
export const getNextPannedZoomState = ({
    dimensions,
    panDelta,
    zoomState,
}: {
    dimensions: ImageModalZoomDimensions;
    panDelta: Point;
    zoomState: ImageModalZoomState;
}): ImageModalZoomState => {
    const nextTranslation = translate(panDelta, zoomState.translation);

    return {
        ...zoomState,
        translation: clampZoomTranslation(nextTranslation, {
            ...zoomState,
            translation: nextTranslation,
            ...dimensions,
        }),
    };
};

/**
 * Returns the default zoom state for the image modal.
 */
export const resetZoomState = (): ImageModalZoomState => ({
    scale: DEFAULT_IMAGE_MODAL_ZOOM_STATE.scale,
    translation: {
        x: DEFAULT_IMAGE_MODAL_ZOOM_STATE.translation.x,
        y: DEFAULT_IMAGE_MODAL_ZOOM_STATE.translation.y,
    },
});
